import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import { STATUS_COLORS } from '../constants/statusColors';
import ModuleNotes from '../components/admin/ModuleNotes';
import { deriveReviewStep, getActiveSteps } from '../components/admin/ReviewModule';
import { WaitingOnStaffQueue, moduleHref } from '../components/admin/guidedReview/StaffQueues';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Map a borrower row's review_sections (+ misc flag) to the current Review
// Module step, mirroring the wizard's own deriveReviewStep so the dashboard
// agrees with the per-borrower Review tab.
function rowStep(b) {
  const sections = Array.isArray(b.review_sections) ? b.review_sections : [];
  const steps = getActiveSteps(!!b.has_misc);
  const step = deriveReviewStep(sections, steps);
  if (step === 'complete') return { num: steps.length + 1, label: 'Complete' };
  return { num: step, label: `Step ${step} — ${steps[step - 1]?.label || ''}` };
}

// ReadyCheck's auto-decisioning replaced the manual in_review step, so files
// arrive at 'approved' (or 'home_shopping' once they start browsing).
// conditional_approval is intentionally not picked up — it isn't one of the
// filter chips below and would otherwise sit in "All" with no way to
// narrow to it.
//
// 'offer_requested' is here so files where the borrower has submitted an
// offer surface to the Loan Team. The Property Module's new Step 3
// (Underwriting Review) waits on the Loan Team to accept every
// review_sections row before the offer can be routed to the listing
// agent — surfacing those files here lets the Loan Team see exactly
// which Property-module-stuck files they're holding up. Treated as
// Urgent so they sit at the top.
const QUALIFYING = ['offer_requested', 'approved', 'home_shopping'];
const URGENT_STAGES = ['offer_requested', 'home_shopping']; // nearing offer — keep momentum

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'urgent', label: 'Urgent' },
  ...QUALIFYING.map((s) => ({ value: s, label: (STATUS_COLORS[s]?.label || s.replace(/_/g, ' ')) })),
];

const daysAgo = (d) => {
  if (!d) return null;
  return Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
};

// ── 🚨 NO CLEAR OR REJECT ON THIS PANEL (Jesse, 2026-10-28: "Remove Reject from both staff panels and send held IDs to
// the fraud queue"). A held ID is a fraud-queue item: the file decision is made there (dismissing the last ID flag clears
// the ID; escalate sends it to underwriting). This panel lists the holds and opens the queue.

export default function AdminModulesUnderwriting() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  // 🔑 ID CHECKS ON HOLD (2026-10-19, item 13): a step-4 ID the automatic check held for possible alteration. Shown at the
  // top of this page, always, so underwriting sees it without being asked — the borrower cannot continue until its flags
  // are decided in the fraud review queue.
  const [idHolds, setIdHolds] = useState([]);
  const [cfHolds, setCfHolds] = useState([]);
  // 🔑 LETTERS TO LOOK AT (Jesse, 2026-10-21 — LOX Tier 1): letters of explanation whose automatic check found they
  // do not (fully) address what they were requested for, or could not read them. Medium; staff only.
  const [loxFlags, setLoxFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  // Keep the row's notes badge in sync as notes are added/removed inline.
  const updateCount = (rowId, n) =>
    setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, notes_count: n } : r)));

  useEffect(() => {
    const token = localStorage.getItem('doorly_admin_token');
    axios.get(`${API}/api/admin/modules/underwriting`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setRows(r.data || []))
      .catch((err) => {
        const status = err.response?.status;
        const msg = err.response?.data?.error || err.message;
        setFetchError(status ? `API ${status}: ${msg}` : msg);
        setRows([]);
      })
      .finally(() => setLoading(false));
    loadIdHolds();
    loadCfHolds();
    loadLoxFlags();
  }, []);

  const loadLoxFlags = () => {
    const token = localStorage.getItem('doorly_admin_token');
    axios.get(`${API}/api/admin/modules/underwriting/lox-review-flags`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setLoxFlags(Array.isArray(r.data) ? r.data : []))
      .catch(() => setLoxFlags([]));
  };

  // 🔑 Approvals held for a 550–559 score until a compensating factor is recorded (Jesse, 2026-10-21).
  const loadCfHolds = () => {
    const token = localStorage.getItem('doorly_admin_token');
    axios.get(`${API}/api/admin/modules/underwriting/comp-factor-holds`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setCfHolds(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCfHolds([]));
  };

  const loadIdHolds = () => {
    const token = localStorage.getItem('doorly_admin_token');
    axios.get(`${API}/api/admin/modules/underwriting/id-holds`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setIdHolds(Array.isArray(r.data) ? r.data : []))
      .catch(() => setIdHolds([]));
  };

  // Column sort. Default (sortKey=null) preserves the existing
  // pipeline-rank ordering. Clicking a column header cycles
  // asc → desc → off so users can return to default without leaving.
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const toggleSort = (key) => {
    if (sortKey !== key) { setSortKey(key); setSortDir('asc'); return; }
    if (sortDir === 'asc') { setSortDir('desc'); return; }
    setSortKey(null); setSortDir('asc');
  };
  const sortIcon = (key) => {
    if (sortKey !== key) return '⇅';
    return sortDir === 'asc' ? '▲' : '▼';
  };

  const term = search.trim().toLowerCase();
  // Pipeline order — most advanced stage first. Borrowers further down
  // the funnel surface at the top of the list so the team can clear
  // the most-time-critical work without scrolling. Within each stage
  // the backend already returns rows by status_updated_at ASC (oldest
  // first), so re-sorting client-side keeps that intra-stage ordering
  // intact — the resorted array is stable for equal stage-rank rows.
  const STAGE_RANK = { offer_requested: 0, home_shopping: 1, approved: 2 };
  let filtered = (filter === 'all'
    ? rows
    : filter === 'urgent'
      ? rows.filter((b) => URGENT_STAGES.includes(b.application_status))
      : rows.filter((b) => b.application_status === filter))
    .filter((b) => !term || `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase().includes(term));

  if (sortKey) {
    const accessors = {
      borrower: (b) => `${b.last_name || ''} ${b.first_name || ''}`.toLowerCase(),
      stage:    (b) => STAGE_RANK[b.application_status] ?? 99,
      days:     (b) => daysAgo(b.last_activity),
      status:   (b) => {
        const total = Number(b.total_sections) || 0;
        const passed = Number(b.passed_sections) || 0;
        return total > 0 ? passed / total : -1;
      },
      step:     (b) => rowStep(b).num,
    };
    const accessor = accessors[sortKey];
    filtered = filtered.slice().sort((a, b) => {
      const va = accessor(a);
      const vb = accessor(b);
      const aN = va == null || Number.isNaN(va);
      const bN = vb == null || Number.isNaN(vb);
      if (aN && bN) return 0;
      if (aN) return 1;
      if (bN) return -1;
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ?  1 : -1;
      return 0;
    });
  } else {
    filtered = filtered.slice().sort((a, b) => {
      const ra = STAGE_RANK[a.application_status] ?? 99;
      const rb = STAGE_RANK[b.application_status] ?? 99;
      return ra - rb;
    });
  }

  const th = { padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e5e7eb' };
  const thBtn = { background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, textTransform: 'inherit', letterSpacing: 'inherit' };
  const td = { padding: '12px 14px', color: '#374151', fontSize: 13, verticalAlign: 'top' };
  const sortable = (label, key) => (
    <button type="button" onClick={() => toggleSort(key)} style={thBtn}>
      <span>{label}</span>
      <span style={{ fontSize: 9, color: sortKey === key ? '#3a6ad9' : '#9ca3af' }}>{sortIcon(key)}</span>
    </button>
  );

  return (
    <AdminLayout>
      <div style={{ padding: '24px 32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>Underwriting Modules</h1>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>Files in review, conditional approval, and shopping</div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {FILTERS.map((f) => (
              <button key={f.value} onClick={() => setFilter(f.value)}
                style={{ padding: '5px 12px', borderRadius: 999, border: `1px solid ${filter === f.value ? '#3a6ad9' : '#e5e7eb'}`, background: filter === f.value ? '#eff6ff' : '#fff', color: filter === f.value ? '#3a6ad9' : '#374151', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* 🔑 WAITING ON STAFF (Jesse, 2026-10-26): every guided borrower blocked on a staff action, urgent first, with the
            button straight to the Module step. The stage list below keeps working. */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <button type="button" data-testid="uw-open-fraud-queue" onClick={() => navigate('/admin/modules/fraud-review')}
            style={{ minHeight: 44, padding: '0 16px', borderRadius: 10, border: '1px solid #000000', background: '#ffffff', color: '#000000', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>Fraud review queue</button>
        </div>
        <WaitingOnStaffQueue token={localStorage.getItem('doorly_admin_token')} navigate={navigate} />

        {idHolds.length > 0 && (
          <div data-testid="uw-id-holds" style={{ border: '1px solid #fecaca', background: '#fef2f2', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#991b1b', marginBottom: 8 }}>
              ID checks on hold ({idHolds.length}) — the borrower cannot authorize the credit check until the ID is decided in the fraud review queue
            </div>
            {idHolds.map((h) => (
              <div key={h.user_id} data-testid="uw-id-hold" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, padding: '6px 0', borderTop: '1px solid #fee2e2', fontSize: 13 }}>
                <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 999, background: '#dc2626', color: '#fff' }}>{String(h.severity || 'high').toUpperCase()}</span>
                <button type="button" onClick={() => navigate(`/admin/borrowers/${h.user_id}?tab=review`)}
                  style={{ background: 'none', border: 0, padding: 0, fontWeight: 700, color: '#111827', cursor: 'pointer', fontSize: 13 }}>{h.name || `Borrower #${h.user_id}`}</button>
                <span style={{ color: '#6b7280' }}>Hold started {h.held_at ? new Date(h.held_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</span>
                <span style={{ color: '#7f1d1d', flex: '1 1 260px', minWidth: 0 }}>{h.description || 'Possible alteration of the uploaded ID.'}</span>
                <button type="button" data-testid={`uw-id-hold-open-queue-${h.user_id}`} onClick={() => navigate('/admin/modules/fraud-review')}
                  style={{ border: '1px solid #d1d5db', background: '#fff', borderRadius: 8, padding: '4px 10px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>Decide in the fraud review queue</button>
              </div>
            ))}
          </div>
        )}
        {cfHolds.length > 0 && (
          <div data-testid="uw-cf-holds" style={{ border: '1px solid #fde68a', background: '#fffbeb', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e', marginBottom: 8 }}>
              Approvals held for a compensating factor ({cfHolds.length}) — record one on the credit-score condition, then approve
            </div>
            {cfHolds.map((h) => (
              <div key={h.id} data-testid={`uw-cf-hold-${h.id}`} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, padding: '6px 0', borderTop: '1px solid #fef3c7', fontSize: 13 }}>
                <button type="button" onClick={() => navigate(`/admin/borrowers/${h.id}?tab=review`)}
                  style={{ background: 'none', border: 0, padding: 0, fontWeight: 700, color: '#111827', cursor: 'pointer', fontSize: 13 }}>{`${h.first_name || ''} ${h.last_name || ''}`.trim() || `Borrower #${h.id}`}</button>
                <span style={{ color: '#6b7280' }}>Held {h.held_at ? new Date(h.held_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</span>
                <span style={{ color: '#92400e' }}>Representative score {h.score ?? '—'} · {h.recorded ?? 0} of {h.required ?? 1} factor recorded</span>
              </div>
            ))}
          </div>
        )}
        {loxFlags.length > 0 && (
          <div data-testid="uw-lox-flags" style={{ border: '1px solid #fde68a', background: '#fffbeb', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e', marginBottom: 8 }}>
              Letters to look at ({loxFlags.length})
            </div>
            {loxFlags.map((f) => {
              const missing = (() => { try { return Array.isArray(f.items_missing) ? f.items_missing : JSON.parse(f.items_missing || '[]'); } catch (_) { return []; } })();
              const label = { partially_addresses: 'Partly addresses the request', does_not_address: 'Does not address the request',
                unreadable: 'Could not be read automatically', could_not_run: 'Check could not run' }[f.verdict] || f.verdict;
              return (
                <div key={f.id} data-testid={`uw-lox-flag-${f.id}`} data-verdict={f.verdict} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, padding: '6px 0', borderTop: '1px solid #fef3c7', fontSize: 13 }}>
                  <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 999, background: '#f59e0b', color: '#fff' }}>{String(f.flag_severity || 'medium').toUpperCase()}</span>
                  <button type="button" onClick={() => navigate(`/admin/borrowers/${f.user_id}?tab=review`)}
                    style={{ background: 'none', border: 0, padding: 0, fontWeight: 700, color: '#111827', cursor: 'pointer', fontSize: 13 }}>{`${f.first_name || ''} ${f.last_name || ''}`.trim() || `Borrower #${f.user_id}`}</button>
                  <span style={{ color: '#374151' }}>{f.title || 'Letter of explanation'}</span>
                  <span style={{ color: '#92400e', fontWeight: 600 }}>{label}</span>
                  {missing.length && !['unreadable', 'could_not_run'].includes(f.verdict) ? <span style={{ color: '#78350f', flex: '1 1 260px', minWidth: 0 }}>Missing: {missing.join(' · ')}</span> : null}
                  <span style={{ color: '#6b7280' }}>{f.created_at ? new Date(f.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</span>
                  {/* 🔑 The letter's actions are on the Module's letters step (Credit) — linked straight there (Jesse, 2026-10-26). */}
                  <button type="button" data-testid={`uw-lox-flag-open-${f.id}`} onClick={() => navigate(moduleHref(f.user_id, 'credit'))}
                    style={{ minHeight: 36, padding: '0 12px', borderRadius: 8, border: 'none', background: '#3a6ad9', color: '#ffffff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Accept or ask again in the Module</button>
                </div>
              );
            })}
          </div>
        )}
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by borrower name…"
          style={{ width: '100%', maxWidth: 360, padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, marginBottom: 16, boxSizing: 'border-box' }}
        />

        {fetchError && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fecaca',
            color: '#991b1b', borderRadius: 10, padding: '12px 16px',
            marginBottom: 16, fontSize: 13,
          }}>
            <strong style={{ marginRight: 6 }}>Failed to load:</strong>{fetchError}
          </div>
        )}

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading…</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 10, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
            <thead><tr style={{ background: '#f9fafb' }}>
              <th style={th}>{sortable('Borrower',      'borrower')}</th>
              <th style={th}>{sortable('Stage',         'stage')}</th>
              <th style={th}>{sortable('Days in Stage', 'days')}</th>
              <th style={th}>{sortable('Module Status', 'status')}</th>
              <th style={th}>{sortable('Current Step',  'step')}</th>
              <th style={th}></th>
            </tr></thead>
            <tbody>
              {filtered.map((b, i) => {
                const isUrgent = URGENT_STAGES.includes(b.application_status);
                const sc = STATUS_COLORS[b.application_status] || { bg: '#f3f4f6', text: '#374151', label: b.application_status };
                const total = Number(b.total_sections) || 0;
                const passed = Number(b.passed_sections) || 0;
                const pct = total > 0 ? Math.round((passed / total) * 100) : 0;
                const d = daysAgo(b.last_activity);
                const expanded = expandedId === b.id;
                const count = Number(b.notes_count) || 0;
                return (
                  <React.Fragment key={b.id}>
                  <tr onClick={() => setExpandedId(expanded ? null : b.id)}
                      style={{ background: expanded ? '#f3f7ff' : i % 2 ? '#fafbfc' : '#fff', borderLeft: isUrgent ? '3px solid #f59e0b' : '3px solid transparent', cursor: 'pointer' }}>
                    <td style={td}>
                      <div style={{ fontWeight: 600, color: '#111827' }}>{`${b.first_name || ''} ${b.last_name || ''}`.trim() || '—'}</div>
                    </td>
                    <td style={td}>
                      <span style={{ padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: sc.bg, color: sc.text }}>
                        {isUrgent ? '⚠ ' : ''}{sc.label}
                      </span>
                    </td>
                    <td style={{ ...td, color: '#6b7280' }}>{d === null ? '—' : `${d}d`}</td>
                    <td style={td}>
                      {/* Module Status — completion of the Underwriting
                          Review module. Bar + N/M readout only; the
                          state pill (Complete / In Progress / Not
                          Started) above the bar was dropped to reduce
                          column noise. The bar's color encoding
                          (green at 100%, blue otherwise, grey track
                          for unstarted) already carries the same
                          signal at a glance. */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 70, height: 6, borderRadius: 999, background: '#e5e7eb', overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: pct === 100 ? '#acf44e' : '#3a6ad9' }} />
                        </div>
                        <span style={{ fontSize: 12, color: '#374151' }}>{total > 0 ? `${pct}% (${passed}/${total})` : '—'}</span>
                      </div>
                    </td>
                    <td style={td}><span style={{ fontWeight: 500, color: '#111827' }}>{rowStep(b).label}</span></td>
                    <td style={td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'flex-end' }}>
                        <span style={{ fontSize: 12, color: count ? '#3a6ad9' : '#9ca3af', fontWeight: count ? 600 : 400 }}>
                          💬 {count}
                        </span>
                        <button onClick={(e) => { e.stopPropagation(); navigate(`/admin/borrowers/${b.id}?tab=review`); }}
                          style={{ background: 'none', border: 'none', color: '#3a6ad9', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
                          View →
                        </button>
                        <span style={{ color: '#9ca3af', fontSize: 11 }}>{expanded ? '▲' : '▼'}</span>
                      </div>
                    </td>
                  </tr>
                  {expanded && (
                    <tr>
                      <td colSpan={6} style={{ padding: 0, borderLeft: isUrgent ? '3px solid #f59e0b' : '3px solid transparent' }}>
                        <div style={{ padding: '16px 20px', background: '#fbfcfd', borderTop: '1px solid #eef0f2' }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>Notes</div>
                          <div style={{ maxWidth: 640 }}>
                            <ModuleNotes moduleType="underwriting" borrowerId={b.id} onCountChange={(n) => updateCount(b.id, n)} />
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>No files in underwriting</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </AdminLayout>
  );
}
